import { cn } from '@/lib/utils';
import { HubertBadge } from 'components/HubertUI/HubertBadge';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { CheckSquare, CircleCheck, CircleX, Edit3, Hash, Info, Star, TriangleAlert } from 'lucide-react';
import type { ComponentProps } from 'react';
import { useMemo, useState } from 'react';
import { useAppSelector } from 'rtk/query/hooks';
import { type TSelectionCountCriteria, EEvaluationType } from '@hubertai/screening-question-types';
import SelectionCountEditor from './SelectionCountEditor';
import { useQuestionAnswerValuesV4 } from 'hooks/useQuestionAnswerValuesV4';
import { useTranslation } from 'react-i18next';

interface Props extends ComponentProps<'div'> {
  nodeId: string;
  editable?: boolean;
}

const getEvaluationBadgeVariant = (evaluationType: EEvaluationType) => {
  switch (evaluationType) {
    case EEvaluationType.Qualified:
      return 'qualified';
    case EEvaluationType.Bonus:
      return 'bonus';
    case EEvaluationType.Warning:
      return 'evaluation-warning';
    case EEvaluationType.Disqualified:
      return 'danger';
    case EEvaluationType.Info:
      return 'info';
    default:
      return 'default';
  }
};

const getEvaluationIcon = (evaluationType: EEvaluationType) => {
  const iconClasses = 'w-4 h-4 mr-2';

  switch (evaluationType) {
    case EEvaluationType.Qualified:
      return <CircleCheck className={cn(iconClasses, 'text-success-800')} />;
    case EEvaluationType.Disqualified:
      return <CircleX className={cn(iconClasses, 'text-danger-800')} />;
    case EEvaluationType.Bonus:
      return <Star className={cn(iconClasses, 'text-white')} />;
    case EEvaluationType.Warning:
      return <TriangleAlert className={cn(iconClasses, 'text-warning-900')} />;
    case EEvaluationType.Info:
      return <Info className={cn(iconClasses, 'text-info-800')} />;
    default:
      return <Hash className={cn(iconClasses)} />;
  }
};

// TODO(i18n): Category 4 — getConditionDescription assembles English sentences from dynamic
// fragments (parts array joined with " and ", conditional prefixes like "NOT:", capitalisation of
// the first character). Cannot be safely extracted until restructured into full translated
// sentences with interpolation, one key per logical branch.
const getConditionDescription = (condition: TSelectionCountCriteria['condition'], idToLabel: Map<string, string>) => {
  const negate = condition.negate ?? false;
  const parts: string[] = [];

  const hasIncludesIds = Array.isArray(condition.includesOptionIds) && condition.includesOptionIds.length > 0;
  const hasOnlyIds = Array.isArray(condition.onlyOptionIds) && condition.onlyOptionIds.length > 0;

  if (hasIncludesIds) {
    const labels = condition.includesOptionIds!.map((id) => idToLabel.get(id) || id).join(', ');
    parts.push(`selection includes [${labels}]`);
  } else if (hasOnlyIds) {
    const labels = condition.onlyOptionIds!.map((id) => idToLabel.get(id) || id).join(', ');
    parts.push(`selection is exactly [${labels}]`);
  }

  if (condition.requiresAllOptions) {
    parts.push('all options are selected');
  }

  if (condition.exactCount) {
    parts.push(`exactly ${condition.exactCount} selected`);
  } else {
    if (condition.min && condition.max) {
      parts.push(`${condition.min}–${condition.max} selected`);
    } else if (condition.min) {
      parts.push(`at least ${condition.min} selected`);
    } else if (condition.max) {
      parts.push(`at most ${condition.max} selected`);
    }
  }

  if (parts.length === 0) {
    return negate ? 'Always (negated, no specific condition)' : 'No specific condition';
  }

  const description = parts.join(' and ');
  if (negate) {
    return `NOT: ${description}`;
  }
  return description.charAt(0).toUpperCase() + description.slice(1);
};

// TODO(i18n): Category 4 — getEvaluationTitle returns dynamic strings built from numeric values
// ("${bonus_points || 0}", "${points || 0} points", "${Math.round(...)}% weight") and a
// capitalised enum name. Cannot be safely extracted until restructured into full translated
// sentences with interpolation per evaluation type.
const getEvaluationTitle = (evaluation: TSelectionCountCriteria['evaluation']) => {
  const { name, bonus_points, points, weight } = evaluation;

  switch (name) {
    case EEvaluationType.Bonus:
      return `${bonus_points || 0}`;
    case EEvaluationType.Point:
      return `${points || 0} points`;
    case EEvaluationType.Weighted:
      return `${Math.round((weight || 0) * 100)}% weight`;
    default:
      return name.charAt(0).toUpperCase() + name.slice(1);
  }
};

const SelectionCountPreview = ({ nodeId, editable = true, className, ...props }: Props) => {
  const { t } = useTranslation('Questionsv2');
  const nodeEvaluation = useAppSelector((state) => state.Template?.present?.c!.evaluation[nodeId]);
  const selectCount = nodeEvaluation?.selection_count ?? ([] as TSelectionCountCriteria[]);
  const [isEditing, setIsEditing] = useState(false);
  const { list } = useQuestionAnswerValuesV4(nodeId);
  const idToLabel = useMemo(() => {
    const map = new Map<string, string>();
    list.forEach((o) => map.set(o.value, o.label));
    return map;
  }, [list]);

  if (isEditing) {
    return (
      <SelectionCountEditor nodeId={nodeId} onClose={() => setIsEditing(false)} className={className} {...props} />
    );
  }

  if (selectCount.length === 0) {
    return (
      <Card className={cn('border-dashed bg-gray-50', className)} {...props}>
        <CardContent className="py-2 text-center">
          <h3 className="mb-1 text-sm font-medium text-gray-900">{t('selectionCount.noSelectionCriteria')}</h3>
          <p className="mb-3 text-xs text-gray-500">{t('selectionCount.addCriteriaHint')}</p>
          {editable && (
            <Button
              onClick={() => setIsEditing(true)}
              size="sm"
              variant="outline"
              className="border-blue-200 text-blue-600 hover:bg-blue-50"
            >
              <Edit3 className="mr-1 h-4 w-4" />
              {t('selectionCount.addCriteria')}
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('', className)} {...props}>
      <CardContent className="p-4">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckSquare className="h-5 w-5 text-gray-600" />
            <h3 className="text-sm font-semibold text-gray-900">{t('selectionCount.heading')}</h3>
            <span className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-500">
              {/* TODO(i18n): Category 5 — plural ternary "{selectCount.length} rule{selectCount.length === 1 ? '' : 's'}" embeds English grammar; restructure into two full translated keys (singular/plural) in a follow-up commit. */}
              {selectCount.length} rule{selectCount.length === 1 ? '' : 's'}
            </span>
          </div>
          {editable && (
            <Button
              onClick={() => setIsEditing(true)}
              size="sm"
              variant="outline"
              className="border-blue-200 text-blue-600 hover:bg-blue-50"
            >
              <Edit3 className="mr-1 h-4 w-4" />
              {t('selectionCount.editButton')}
            </Button>
          )}
        </div>

        <div className="space-y-3">
          {selectCount.map((criteria: TSelectionCountCriteria, index: number) => (
            <div
              key={index}
              className="flex items-start gap-3 rounded-lg border border-gray-100 bg-gray-50 p-3 transition-colors hover:border-gray-200"
            >
              <div className="mt-0.5 flex-shrink-0">
                <HubertBadge
                  variant={getEvaluationBadgeVariant(criteria.evaluation.name)}
                  className="flex items-center text-xs font-medium"
                >
                  {getEvaluationIcon(criteria.evaluation.name)}
                  {getEvaluationTitle(criteria.evaluation)}
                </HubertBadge>
              </div>

              <div className="min-w-0 flex-1">
                <p className="mb-1 text-sm font-medium text-gray-900">
                  {getConditionDescription(criteria.condition, idToLabel)}
                </p>

                {/* Additional details for specific conditions */}
                <div className="flex items-center gap-4 text-xs text-gray-600">
                  {!!criteria.condition.min && (
                    <span className="flex items-center gap-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-blue-400"></span>
                      {t('selectionCount.min', { value: criteria.condition.min })}
                    </span>
                  )}
                  {!!criteria.condition.max && (
                    <span className="flex items-center gap-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-orange-400"></span>
                      {t('selectionCount.max', { value: criteria.condition.max })}
                    </span>
                  )}
                  {!!criteria.condition.exactCount && (
                    <span className="flex items-center gap-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-green-400"></span>
                      {t('selectionCount.exact', { value: criteria.condition.exactCount })}
                    </span>
                  )}
                  {criteria.condition.requiresAllOptions && (
                    <span className="flex items-center gap-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-purple-400"></span>
                      {t('selectionCount.allRequired')}
                    </span>
                  )}
                  {Array.isArray(criteria.condition.onlyOptionIds) && criteria.condition.onlyOptionIds.length > 0 && (
                    <span className="flex items-center gap-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-pink-400"></span>
                      {t('selectionCount.exactly', {
                        labels: criteria.condition.onlyOptionIds.map((id) => idToLabel.get(id) || id).join(', '),
                      })}
                    </span>
                  )}
                  {Array.isArray(criteria.condition.includesOptionIds) &&
                    criteria.condition.includesOptionIds.length > 0 && (
                      <span className="flex items-center gap-1">
                        <span className="h-1.5 w-1.5 rounded-full bg-teal-400"></span>
                        {t('selectionCount.includes', {
                          labels: criteria.condition.includesOptionIds.map((id) => idToLabel.get(id) || id).join(', '),
                        })}
                      </span>
                    )}
                  {Array.isArray(criteria.condition.ignoreOptionIds) &&
                    criteria.condition.ignoreOptionIds.length > 0 && (
                      <span className="flex items-center gap-1">
                        <span className="h-1.5 w-1.5 rounded-full bg-slate-400"></span>
                        {t('selectionCount.ignores', {
                          labels: criteria.condition.ignoreOptionIds.map((id) => idToLabel.get(id) || id).join(', '),
                        })}
                      </span>
                    )}
                  {criteria.condition.negate && (
                    <span className="flex items-center gap-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-red-400"></span>
                      {t('selectionCount.negated')}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default SelectionCountPreview;
